// --- Aurora Platform ---

export type AuroraPlatform = 'je' | 'spartanx' | 'lgcy';

export const AURORA_PLATFORMS: AuroraPlatform[] = ['je', 'spartanx', 'lgcy'];

export const AURORA_PLATFORM_OPTIONS: { key: AuroraPlatform; label: string }[] = [
  { key: 'je', label: 'JE' },
  { key: 'spartanx', label: 'SpartanX' },
  { key: 'lgcy', label: 'LGCY' },
];

export const DEFAULT_AURORA_PLATFORM: AuroraPlatform = 'je';

// --- Structures ---

export const MAX_STRUCTURES = 3;

// --- System Keys & Labels ---

export type SystemKey = 'system_one' | 'system_two' | 'system_three';

export const SYSTEM_KEYS: SystemKey[] = ['system_one', 'system_two', 'system_three'];

export const SYSTEM_LABELS: Record<SystemKey, string> = {
  system_one: 'System 1',
  system_two: 'System 2',
  system_three: 'System 3',
};

// --- System Configuration ---

export const DISPLAY_SYSTEM_COUNT = 3;

export type ItemCategory = 'modules' | 'inverters' | 'microinverters' | 'optimizers' | 'combiners';

export const ITEM_CATEGORIES: ItemCategory[] = ['modules', 'inverters', 'microinverters', 'optimizers', 'combiners'];

export interface SystemColumnStyle {
  label: string;
  bg: string;
  border: string;
  headerBg: string;
  headerText: string;
  badgeBg: string;
  badgeText: string;
  empty: string;
}

export const SYSTEM_COLUMNS: SystemColumnStyle[] = [
  { label: 'System 1', bg: 'bg-blue-50/30', border: 'border-blue-200/50', headerBg: 'bg-gradient-to-r from-blue-600 to-blue-700', headerText: 'text-white', badgeBg: 'bg-white/20', badgeText: 'text-blue-100', empty: 'text-blue-200' },
  { label: 'System 2', bg: 'bg-blue-50/30', border: 'border-blue-200/50', headerBg: 'bg-gradient-to-r from-blue-600 to-blue-700', headerText: 'text-white', badgeBg: 'bg-white/20', badgeText: 'text-blue-100', empty: 'text-blue-200' },
  { label: 'System 3', bg: 'bg-blue-50/30', border: 'border-blue-200/50', headerBg: 'bg-gradient-to-r from-blue-600 to-blue-700', headerText: 'text-white', badgeBg: 'bg-white/20', badgeText: 'text-blue-100', empty: 'text-blue-200' },
];

export interface ComponentLabelConfig {
  label: string;
  color: string;
  icon: string;
}

export const COMPONENT_LABELS: Record<ItemCategory, ComponentLabelConfig> = {
  modules: { label: 'Module', color: 'text-violet-600 bg-violet-50 border-violet-200', icon: 'M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z' },
  inverters: { label: 'Inverter', color: 'text-blue-600 bg-blue-50 border-blue-200', icon: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z' },
  microinverters: { label: 'Microinverter', color: 'text-cyan-600 bg-cyan-50 border-cyan-200', icon: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z' },
  optimizers: { label: 'Optimizer', color: 'text-amber-600 bg-amber-50 border-amber-200', icon: 'M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75' },
  combiners: { label: 'Combiner', color: 'text-emerald-600 bg-emerald-50 border-emerald-200', icon: 'M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 004.5 9v.878m13.5-3A2.25 2.25 0 0119.5 9v.878m0 0a2.246 2.246 0 00-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0121 12v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6c0-.98.626-1.813 1.5-2.122' },
};

export interface CategoryRowConfig {
  key: string;
  label: string;
  categories: ItemCategory[];
}

export const CATEGORY_ROWS: CategoryRowConfig[] = [
  { key: 'module', label: 'MODULE', categories: ['modules'] },
  { key: 'inverter', label: 'INVERTERS / MICROINVERTERS', categories: ['inverters', 'microinverters'] },
  { key: 'optimizer', label: 'OPTIMIZER', categories: ['optimizers'] },
  { key: 'combiner', label: 'COMBINER', categories: ['combiners'] },
];

// --- Workflow Steps ---

export type WorkflowStep = 1 | 2 | 3 | 4;

export const TOTAL_WORKFLOW_STEPS = 4;

export const WORKFLOW_STEPS: { step: WorkflowStep; label: string }[] = [
  { step: 1, label: 'Review Equipment Matching' },
  { step: 2, label: 'System Configuration' },
  { step: 3, label: 'Roof Structures' },
  { step: 4, label: 'Panel Rows Review' },
];
